import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { DatePicker } from "@/components/ui/DatePicker";
import { Modal } from "@/components/ui/Modal";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import {
  useOneTimeCosts,
  useRecurringCosts,
  useCreateOneTimeCost,
  useCreateRecurringCost,
  useDeleteOneTimeCost,
  useDeleteRecurringCost,
  useMarkOneTimeCostPaid,
  useUpdateOneTimeCost,
  useOneTimeCostAllocations,
  useSetOneTimeCostAllocations,
  useResetOneTimeCostAllocations,
  useRecurringCostAllocations,
  useSetRecurringCostAllocations,
  useResetRecurringCostAllocations,
  useUpdateRecurringCost,
  useUploadOneTimeCostInvoice,
  useDeleteOneTimeCostInvoice,
  useRecurringCostInvoices,
  useUnskipRecurringMonth,
} from "@/features/finances/hooks/useFinances";
import { useCompanies } from "@/features/group_finance/hooks/useGroupFinance";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import type { InternalCostInvoice, InternalCostRecurring } from "@/features/finances/types";
import { EXPENSE_STATUS_LABELS, EXPENSE_STATUS_COLORS } from "@/features/finances/types";
import { EditRecurringCostModal } from "@/features/finances/components/EditRecurringCostModal";
import { MissingRecurringInvoicesPanel } from "@/features/finances/components/MissingRecurringInvoicesPanel";

interface Project { id: string; name: string; short_name: string; total_value: string | null; company: string | null; }

type Tab = "one_time" | "recurring";

export function InternalCostsPage() {
  const { user } = useAuthStore();
  const canManageInvoices = !!(user?.is_ceo || user?.role === "accountant");

  const [tab, setTab] = useState<Tab>("one_time");
  const [filterProject, setFilterProject] = useState<string | "all">("all");
  const [formProject, setFormProject] = useState("");
  // Track which cost row has allocation panel expanded: "ot:<id>" or "rc:<id>"
  const [expandedAlloc, setExpandedAlloc] = useState<string | null>(null);
  // Track which recurring cost has invoice panel expanded: recurring cost id
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => apiClient.get<{ data: Project[] }>("/projects/").then((r) => r.data.data),
  });

  const projectParam = filterProject !== "all" ? filterProject : undefined;
  const { data: oneTimeCosts = [], isLoading } = useOneTimeCosts(projectParam);
  const { data: recurringCosts = [] } = useRecurringCosts(projectParam);

  const createOT = useCreateOneTimeCost(formProject || undefined);
  const createRC = useCreateRecurringCost(formProject || undefined);
  const deleteOT = useDeleteOneTimeCost(formProject || undefined);
  const deleteRC = useDeleteRecurringCost(formProject || undefined);
  const markPaid = useMarkOneTimeCostPaid(formProject || undefined);
  const updateOT = useUpdateOneTimeCost();
  const uploadOTInvoice = useUploadOneTimeCostInvoice(projectParam);
  const deleteOTInvoice = useDeleteOneTimeCostInvoice(projectParam);

  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);
  const [editingDatesId, setEditingDatesId] = useState<string | null>(null);
  const [datesDraft, setDatesDraft] = useState<{ start_date: string; end_date: string }>({ start_date: "", end_date: "" });
  const [editingCost, setEditingCost] = useState<InternalCostRecurring | null>(null);
  const updateRC = useUpdateRecurringCost();

  const todayIso = () => new Date().toISOString().slice(0, 10);
  const [payModal, setPayModal] = useState<{ id: string; date: string } | null>(null);

  const { data: companies = [] } = useCompanies(
    user?.is_ceo || user?.role === "accountant" || !!user?.has_group_finance_access,
  );

  // Default company = first one whose name/code contains "ELEKTIM", else first active
  const defaultCompanyId = companies.find(
    (c) => c.short_code.includes("ELEKTIM") || c.name.toLowerCase().includes("elektim")
  )?.id ?? companies[0]?.id ?? "";

  const [otForm, setOTForm] = useState({ description: "", amount: "", cost_date: "", company: "" });
  const [otFile, setOTFile] = useState<File | null>(null);
  const [otErr, setOTErr] = useState<string | null>(null);

  const emptyRC = { description: "", day_of_month: "1", start_date: "", end_date: "", company: "" };
  const [rcForm, setRCForm] = useState(emptyRC);

  // Set ELEKTIM as default once companies load
  useEffect(() => {
    if (defaultCompanyId) {
      setOTForm((f) => (f.company ? f : { ...f, company: defaultCompanyId }));
      setRCForm((f) => (f.company ? f : { ...f, company: defaultCompanyId }));
    }
  }, [defaultCompanyId]);

  // When project is selected, force its associated company
  useEffect(() => {
    if (!formProject) {
      // Back to "no project" — restore default company
      setOTForm((f) => ({ ...f, company: defaultCompanyId }));
      setRCForm((f) => ({ ...f, company: defaultCompanyId }));
      return;
    }
    const project = projects.find((p) => p.id === formProject);
    if (project?.company) {
      setOTForm((f) => ({ ...f, company: project.company! }));
      setRCForm((f) => ({ ...f, company: project.company! }));
    }
  }, [formProject, projects]);
  const [rcHasEndDate, setRCHasEndDate] = useState(false);
  const [rcErr, setRCErr] = useState<string | null>(null);

  const handleCreateOT = async () => {
    if (!otForm.description.trim() || !otForm.amount || !otForm.cost_date) {
      setOTErr("Opis, kwota i data są wymagane.");
      return;
    }
    try {
      const created = await createOT.mutateAsync({ project: formProject || null, company: otForm.company || null, description: otForm.description, amount: otForm.amount, cost_date: otForm.cost_date } as never);
      let uploadFailed = false;
      if (otFile && created?.id) {
        try {
          await uploadOTInvoice.mutateAsync({ costId: created.id, file: otFile });
        } catch {
          uploadFailed = true;
        }
      }
      setOTForm({ description: "", amount: "", cost_date: "", company: defaultCompanyId });
      setOTFile(null);
      setOTErr(uploadFailed ? "Koszt zapisano, ale nie udało się przesłać faktury PDF — dodaj ją z listy." : null);
    } catch { setOTErr("Błąd zapisu."); }
  };

  const handleCreateRC = async () => {
    // Recurring costs no longer carry a fixed amount — it comes from the invoices
    // added later in /expenses (faktury comiesięczne).
    if (!rcForm.description.trim() || !rcForm.start_date) {
      setRCErr("Opis i data startu są wymagane.");
      return;
    }
    const day = parseInt(rcForm.day_of_month);
    if (isNaN(day) || day < 1 || day > 31) { setRCErr("Dzień miesiąca musi być 1–31."); return; }
    try {
      await createRC.mutateAsync({
        project: formProject || null,
        company: rcForm.company || null,
        description: rcForm.description,
        day_of_month: day,
        start_date: rcForm.start_date,
        end_date: rcHasEndDate ? (rcForm.end_date || "2099-12-31") : "2099-12-31",
        is_active: true,
      } as never);
      setRCForm({ ...emptyRC, company: defaultCompanyId });
      setRCHasEndDate(false);
      setRCErr(null);
    } catch { setRCErr("Błąd zapisu."); }
  };

  if (isLoading) return <PageLoader />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between">
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Koszty wewnętrzne</h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Jednorazowe i stałe koszty wewnętrzne spółek GK — podstawa do kalkulacji kosztów projektów.
          </p>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        {/* Left panel — add form */}
        <div className="w-full lg:w-72 lg:flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-4">Nowy koszt wewnętrzny</h2>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex flex-col gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Przedsięwzięcie</label>
              <select
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                value={formProject}
                onChange={(e) => setFormProject(e.target.value)}
              >
                <option value="">— bez przypisania (koszt ogólny) —</option>
                {[...projects].sort((a, b) => a.short_name.localeCompare(b.short_name)).map((p) => (
                  <option key={p.id} value={p.id}>{p.short_name} — {p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Spółka GK *</label>
              <div
                title={formProject ? "Spółka GK jest narzucona przez wybrane przedsięwzięcie i nie można jej zmienić." : undefined}
                className={formProject ? "cursor-not-allowed" : undefined}
              >
                <select
                  disabled={!!formProject}
                  className={`w-full border border-gray-300 rounded px-2 py-1.5 text-sm ${formProject ? "opacity-50 pointer-events-none bg-gray-100" : ""}`}
                  value={tab === "one_time" ? otForm.company : rcForm.company}
                  onChange={(e) =>
                    tab === "one_time"
                      ? setOTForm({ ...otForm, company: e.target.value })
                      : setRCForm({ ...rcForm, company: e.target.value })
                  }
                >
                  <option value="">— bez przypisania —</option>
                  {[...companies].sort((a, b) => a.short_code.localeCompare(b.short_code)).map((c) => (
                    <option key={c.id} value={c.id}>{c.short_code} — {c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-1 border-b border-orange-200 pb-2">
              <button
                className={`text-xs px-3 py-1 rounded ${tab === "one_time" ? "bg-orange-200 text-orange-800 font-medium" : "text-gray-500 dark:text-gray-400"}`}
                onClick={() => setTab("one_time")}
              >Jednorazowy</button>
              <button
                className={`text-xs px-3 py-1 rounded ${tab === "recurring" ? "bg-orange-200 text-orange-800 font-medium" : "text-gray-500 dark:text-gray-400"}`}
                onClick={() => setTab("recurring")}
              >Stały</button>
            </div>

            {tab === "one_time" ? (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Opis *</label>
                  <input className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    value={otForm.description} onChange={(e) => setOTForm({ ...otForm, description: e.target.value })}
                    placeholder="np. Zakup sprzętu" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Kwota (PLN) *</label>
                  <input type="number" step="0.01" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    value={otForm.amount} onChange={(e) => setOTForm({ ...otForm, amount: e.target.value })} placeholder="0.00" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Data kosztu *</label>
                  <DatePicker
                    value={otForm.cost_date} onChange={(v) => setOTForm({ ...otForm, cost_date: v })} />
                </div>
                {canManageInvoices && (
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Faktura PDF</label>
                    <FileDropZone
                      accept="application/pdf"
                      files={otFile ? [otFile] : []}
                      onFilesChange={(fs) => setOTFile(fs[0] ?? null)}
                    />
                  </div>
                )}
                {otErr && <p className="text-xs text-red-600">{otErr}</p>}
                <Button loading={createOT.isPending || uploadOTInvoice.isPending} onClick={handleCreateOT}>Zapisz koszt jednorazowy</Button>
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Opis *</label>
                  <input className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    value={rcForm.description} onChange={(e) => setRCForm({ ...rcForm, description: e.target.value })}
                    placeholder="np. Wynajem biura" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Dzień miesiąca *</label>
                  <input type="number" min="1" max="31" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    value={rcForm.day_of_month} onChange={(e) => setRCForm({ ...rcForm, day_of_month: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Data startu *</label>
                    <DatePicker
                      value={rcForm.start_date} onChange={(v) => setRCForm({ ...rcForm, start_date: v })} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Data końcowa</label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 mb-1 cursor-pointer">
                      <input type="checkbox" checked={rcHasEndDate} onChange={(e) => setRCHasEndDate(e.target.checked)} />
                      Ustaw datę końcową
                    </label>
                    {rcHasEndDate && (
                      <DatePicker
                        value={rcForm.end_date} onChange={(v) => setRCForm({ ...rcForm, end_date: v })} />
                    )}
                  </div>
                </div>
                {rcErr && <p className="text-xs text-red-600">{rcErr}</p>}
                <Button loading={createRC.isPending} onClick={handleCreateRC}>Zapisz koszt stały</Button>
              </>
            )}
          </div>
        </div>

        {/* Right panel — lists */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs text-gray-500 dark:text-gray-400">Filtruj przedsięwzięcie:</span>
            <select className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 min-w-0 max-w-xs truncate"
              value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
              <option value="all">Wszystkie</option>
              <option value="">Bez przypisania</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.short_name} — {p.name}</option>
              ))}
            </select>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Koszty jednorazowe</h3>
          {oneTimeCosts.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">Brak jednorazowych kosztów wewnętrznych.</p>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="lg:hidden flex flex-col gap-2 mb-6">
                {oneTimeCosts.map((c) => {
                  const key = `ot:${c.id}`;
                  const isExpanded = expandedAlloc === key;
                  return (
                    <div key={c.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          {c.source_expense_id && (
                            <span className="inline-block text-xs bg-[var(--color-primary-dim)] text-[var(--color-primary)] border border-[var(--color-primary)] rounded px-1.5 py-0.5 mr-1 mb-1 whitespace-nowrap">
                              Fakt. kosztowa
                            </span>
                          )}
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200 break-words">{c.description}</p>
                          <p className="text-xs mt-0.5">
                            {c.project_name
                              ? <span className="text-gray-500 dark:text-gray-400">{c.project_name}</span>
                              : <span className="italic text-orange-500">ogólny</span>}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-red-700 whitespace-nowrap">-{formatAmount(c.amount)} PLN</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(c.cost_date)}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-2 gap-y-1 items-center">
                          <span>{c.company_name ?? "—"}</span>
                          {c.is_paid ? (
                            <span className="text-green-600 font-medium">Opłacono {formatDate(c.paid_at, "")}</span>
                          ) : (
                            <button onClick={() => setPayModal({ id: c.id, date: todayIso() })} className="text-[var(--color-primary)] hover:underline">
                              Oznacz jako opłacone
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                          {canManageInvoices && (
                            <OneTimeCostInvoiceCell
                              invoice={c.invoice}
                              onUpload={(file) => uploadOTInvoice.mutate({ costId: c.id, file })}
                              onDelete={() => deleteOTInvoice.mutate(c.id)}
                              uploading={uploadOTInvoice.isPending}
                              deleting={deleteOTInvoice.isPending}
                            />
                          )}
                          {!c.project && (
                            <button
                              onClick={() => setExpandedAlloc(isExpanded ? null : key)}
                              className={`text-xs px-2 py-0.5 rounded border ${isExpanded ? "bg-orange-100 border-orange-300 text-orange-700" : "border-gray-300 text-gray-500 dark:text-gray-400"}`}
                            >
                              {isExpanded ? "Zwiń" : "Dyspozycja"}
                            </button>
                          )}
                          <button onClick={() => { if (confirm("Usunąć koszt?")) deleteOT.mutate(c.id); }} className="text-xs text-red-500 hover:underline">Usuń</button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="mt-2 pt-2 border-t border-orange-200 -mx-3 -mb-3 px-3 pb-3 bg-orange-50 rounded-b-lg">
                          <CostAllocationPanel costId={c.id} costType="one_time" projects={projects} totalAmount={parseFloat(c.amount)} unit="PLN" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop table */}
              <div className="hidden lg:block bg-white dark:bg-gray-800 overflow-x-auto mb-6">
                <table className="w-full text-xs min-w-[500px]">
                  <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Opis</th>
                      <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Przedsięwzięcie</th>
                      <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Spółka</th>
                      <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data</th>
                      <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kwota</th>
                      <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Opłacenie</th>
                      {canManageInvoices && <th className="px-3 py-2 w-24 text-center font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">FV PDF</th>}
                      <th className="px-3 py-2 w-28 text-center font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Dyspozycja</th>
                      <th className="px-3 py-2 w-16 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                    </tr>
                  </thead>
                  <tbody>
                    {oneTimeCosts.map((c) => {
                      const key = `ot:${c.id}`;
                      const isExpanded = expandedAlloc === key;
                      return (
                        <>
                          <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100">
                            <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                              {c.source_expense_id && (
                                <span className="inline-block text-xs bg-[var(--color-primary-dim)] text-[var(--color-primary)] border border-[var(--color-primary)] rounded px-1.5 py-0.5 mr-1.5 mb-0.5 whitespace-nowrap">
                                  Fakt. kosztowa
                                </span>
                              )}
                              {c.description}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 max-w-[180px]">
                              <span className="block truncate" title={c.project_name ?? undefined}>
                                {c.project_name ?? <span className="italic text-orange-500">ogólny</span>}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                              {editingCompanyId === c.id ? (
                                <select
                                  autoFocus
                                  className="border border-orange-300 rounded px-1.5 py-0.5 text-xs bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                                  defaultValue={c.company ?? ""}
                                  disabled={updateOT.isPending}
                                  onChange={async (e) => {
                                    await updateOT.mutateAsync({ id: c.id, data: { company: e.target.value || null } as never });
                                    setEditingCompanyId(null);
                                  }}
                                  onBlur={() => setEditingCompanyId(null)}
                                >
                                  <option value="">— brak —</option>
                                  {[...companies].sort((a, b) => a.short_code.localeCompare(b.short_code)).map((co) => (
                                    <option key={co.id} value={co.id}>{co.short_code}</option>
                                  ))}
                                </select>
                              ) : (
                                <button
                                  className="group flex items-center gap-1 hover:text-orange-600 transition-colors"
                                  title="Kliknij, aby zmienić spółkę"
                                  onClick={() => setEditingCompanyId(c.id)}
                                >
                                  <span>{c.company_name ?? <span className="text-gray-300">—</span>}</span>
                                  <span className="opacity-0 group-hover:opacity-100 text-orange-400 text-[10px]">✎</span>
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{formatDate(c.cost_date)}</td>
                            <td className="px-3 py-2 text-right font-medium text-red-700">-{formatAmount(c.amount)} PLN</td>
                            <td className="px-3 py-2">
                              {c.is_paid ? (
                                <span className="text-xs text-green-700 font-medium">
                                  Opłacono {formatDate(c.paid_at, "")}
                                </span>
                              ) : (
                                <button
                                  onClick={() => setPayModal({ id: c.id, date: todayIso() })}
                                  className="text-xs text-[var(--color-primary)] hover:underline"
                                >
                                  Oznacz jako opłacone
                                </button>
                              )}
                            </td>
                            {canManageInvoices && (
                              <td className="px-3 py-2 text-center">
                                <OneTimeCostInvoiceCell
                                  invoice={c.invoice}
                                  onUpload={(file) => uploadOTInvoice.mutate({ costId: c.id, file })}
                                  onDelete={() => deleteOTInvoice.mutate(c.id)}
                                  uploading={uploadOTInvoice.isPending}
                                  deleting={deleteOTInvoice.isPending}
                                />
                              </td>
                            )}
                            <td className="px-3 py-2 text-center">
                              {!c.project && (
                                <button
                                  onClick={() => setExpandedAlloc(isExpanded ? null : key)}
                                  className={`text-xs px-2 py-0.5 rounded border ${isExpanded ? "bg-orange-100 border-orange-300 text-orange-700" : "border-gray-300 text-gray-500 dark:text-gray-400 hover:border-orange-300 hover:text-orange-600"}`}
                                >
                                  {isExpanded ? "Zwiń" : "Dyspozycja"}
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button onClick={() => { if (confirm("Usunąć koszt?")) deleteOT.mutate(c.id); }}
                                className="text-xs text-red-500 hover:underline">Usuń</button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr key={`${c.id}-alloc`}>
                              <td colSpan={canManageInvoices ? 8 : 7} className="px-3 pb-3 bg-orange-50">
                                <CostAllocationPanel
                                  costId={c.id}
                                  costType="one_time"
                                  projects={projects}
                                  totalAmount={parseFloat(c.amount)}
                                  unit="PLN"
                                />
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Braki faktur comiesięcznych — miesiąc bez faktury leci na średniej,
              więc bez tego panelu nikt nie wie, że kwota jest orientacyjna. */}
          <MissingRecurringInvoicesPanel />

          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Koszty stałe comiesięczne</h3>
          {recurringCosts.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">Brak kosztów stałych.</p>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="lg:hidden flex flex-col gap-2">
                {recurringCosts.map((c) => {

                  const key = `rc:${c.id}`;
                  const isExpanded = expandedAlloc === key;
                  const endDisplay = (!c.end_date || c.end_date >= "2099-01-01") ? "∞" : formatDate(c.end_date);
                  return (
                    <div key={c.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200 break-words">{c.description}</p>
                          <p className="text-xs mt-0.5">
                            {c.project_name
                              ? <span className="text-gray-500 dark:text-gray-400">{c.project_name}</span>
                              : <span className="italic text-orange-500">ogólny</span>}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-red-700 whitespace-nowrap">{formatAmount(c.average_amount)} PLN netto/mies.</p>
                          <Badge color={c.is_active ? "green" : "gray"}>{c.is_active ? "Aktywny" : "Nieaktywny"}</Badge>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {c.company_name ?? "—"} · {formatDate(c.start_date)} — {endDisplay} · {c.day_of_month}. dzień mies.
                        </p>
                        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                          {canManageInvoices && (
                            <button
                              onClick={() => setExpandedInvoice(expandedInvoice === c.id ? null : c.id)}
                              className={`text-xs px-2 py-0.5 rounded border ${expandedInvoice === c.id ? "bg-blue-100 border-blue-300 text-blue-700" : "border-gray-300 text-gray-500 dark:text-gray-400"}`}
                            >
                              {expandedInvoice === c.id ? "Zwiń FV" : "Faktury PDF"}
                            </button>
                          )}
                          {!c.project && (
                            <button
                              onClick={() => setExpandedAlloc(isExpanded ? null : key)}
                              className={`text-xs px-2 py-0.5 rounded border ${isExpanded ? "bg-orange-100 border-orange-300 text-orange-700" : "border-gray-300 text-gray-500 dark:text-gray-400"}`}
                            >
                              {isExpanded ? "Zwiń" : "Dyspozycja"}
                            </button>
                          )}
                          <button onClick={() => setEditingCost(c)} className="text-xs text-[var(--color-primary)] hover:underline">Edytuj</button>
                          <button onClick={() => { if (confirm("Usunąć koszt stały?")) deleteRC.mutate(c.id); }} className="text-xs text-red-500 hover:underline">Usuń</button>
                        </div>
                      </div>
                      {expandedInvoice === c.id && canManageInvoices && (
                        <div className="mt-2 pt-2 border-t border-blue-200 -mx-3 -mb-3 px-3 pb-3 bg-blue-50 dark:bg-blue-950/20 rounded-b-lg">
                          <RecurringCostInvoicePanel cost={c} />
                        </div>
                      )}
                      {isExpanded && (
                        <div className="mt-2 pt-2 border-t border-orange-200 -mx-3 -mb-3 px-3 pb-3 bg-orange-50 rounded-b-lg">
                          <CostAllocationPanel costId={c.id} costType="recurring" projects={projects} totalAmount={parseFloat(c.average_amount)} unit="PLN/mies." />
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border-t-2 border-gray-300 dark:border-gray-600 rounded-b-lg">
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-widest">Suma</span>
                  <span className="text-sm font-bold text-red-700">
                    {formatAmount(recurringCosts.reduce((sum, c) => sum + parseFloat(c.average_amount), 0).toFixed(2))} PLN/mies.
                  </span>
                </div>
              </div>

              {/* Desktop table */}
              <div className="hidden lg:block bg-white dark:bg-gray-800 overflow-x-auto">
                <table className="w-full text-xs min-w-[500px]">
                  <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Opis</th>
                      <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Przedsięwzięcie</th>
                      <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Spółka</th>
                      <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Śr. netto z faktur/mies.</th>
                      <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Dzień</th>
                      <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Od — Do</th>
                      <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Aktywny</th>
                      {canManageInvoices && <th className="px-3 py-2 w-24 text-center font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">FV PDF</th>}
                      <th className="px-3 py-2 w-28 text-center font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Dyspozycja</th>
                      <th className="px-3 py-2 w-16 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                    </tr>
                  </thead>
                  <tbody>
                    {recurringCosts.map((c) => {
                      const key = `rc:${c.id}`;
                      const isExpanded = expandedAlloc === key;
                      return (
                        <>
                          <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100">
                            <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{c.description}</td>
                            <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 max-w-[180px]">
                              <span className="block truncate" title={c.project_name ?? undefined}>
                                {c.project_name ?? <span className="italic text-orange-500">ogólny</span>}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                              {c.company_name ?? <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-red-700">{formatAmount(c.average_amount)}</td>
                            <td className="px-3 py-2 text-center text-gray-500 dark:text-gray-400">{c.day_of_month}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                              {editingDatesId === c.id ? (
                                <div className="flex flex-col gap-1 min-w-[220px]">
                                  <div className="flex items-center gap-1">
                                    <label className="text-xs text-gray-500 w-8 shrink-0">Od</label>
                                    <DatePicker
                                      className="border-orange-300"
                                      value={datesDraft.start_date}
                                      onChange={(v) => setDatesDraft((d) => ({ ...d, start_date: v }))}
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <label className="text-xs text-gray-500 w-8 shrink-0">Do</label>
                                    <DatePicker
                                      className="border-orange-300"
                                      value={datesDraft.end_date}
                                      onChange={(v) => setDatesDraft((d) => ({ ...d, end_date: v }))}
                                      placeholder="brak (∞)"
                                    />
                                  </div>
                                  <div className="flex gap-1 mt-0.5">
                                    <button
                                      disabled={updateRC.isPending}
                                      onClick={async () => {
                                        await updateRC.mutateAsync({
                                          id: c.id,
                                          data: {
                                            start_date: datesDraft.start_date,
                                            end_date: datesDraft.end_date || null,
                                          } as never,
                                        });
                                        setEditingDatesId(null);
                                      }}
                                      className="text-xs px-2 py-0.5 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
                                    >
                                      Zapisz
                                    </button>
                                    <button
                                      onClick={() => setEditingDatesId(null)}
                                      className="text-xs px-2 py-0.5 border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
                                    >
                                      Anuluj
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  className="group flex items-center gap-1 hover:text-orange-600 transition-colors"
                                  title="Kliknij, aby edytować daty"
                                  onClick={() => {
                                    setEditingDatesId(c.id);
                                    setDatesDraft({
                                      start_date: c.start_date,
                                      end_date: (!c.end_date || c.end_date >= "2099-01-01") ? "" : c.end_date,
                                    });
                                  }}
                                >
                                  <span>{formatDate(c.start_date)} — {(!c.end_date || c.end_date >= "2099-01-01") ? "∞" : formatDate(c.end_date)}</span>
                                  <span className="opacity-0 group-hover:opacity-100 text-orange-400 text-[10px]">✎</span>
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Badge color={c.is_active ? "green" : "gray"}>{c.is_active ? "Tak" : "Nie"}</Badge>
                            </td>
                            {canManageInvoices && (
                              <td className="px-3 py-2 text-center">
                                <button
                                  onClick={() => setExpandedInvoice(expandedInvoice === c.id ? null : c.id)}
                                  className={`text-xs px-2 py-0.5 rounded border ${expandedInvoice === c.id ? "bg-blue-100 border-blue-300 text-blue-700" : "border-gray-300 text-gray-500 dark:text-gray-400 hover:border-blue-300 hover:text-blue-600"}`}
                                >
                                  {expandedInvoice === c.id ? "Zwiń" : "Faktury"}
                                </button>
                              </td>
                            )}
                            <td className="px-3 py-2 text-center">
                              {!c.project && (
                                <button
                                  onClick={() => setExpandedAlloc(isExpanded ? null : key)}
                                  className={`text-xs px-2 py-0.5 rounded border ${isExpanded ? "bg-orange-100 border-orange-300 text-orange-700" : "border-gray-300 text-gray-500 dark:text-gray-400 hover:border-orange-300 hover:text-orange-600"}`}
                                >
                                  {isExpanded ? "Zwiń" : "Dyspozycja"}
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <button onClick={() => setEditingCost(c)}
                                className="text-xs text-[var(--color-primary)] hover:underline mr-2">Edytuj</button>
                              <button onClick={() => { if (confirm("Usunąć koszt stały?")) deleteRC.mutate(c.id); }}
                                className="text-xs text-red-500 hover:underline">Usuń</button>
                            </td>
                          </tr>
                          {expandedInvoice === c.id && canManageInvoices && (
                            <tr key={`${c.id}-inv`}>
                              <td colSpan={canManageInvoices ? 10 : 9} className="px-3 pb-3 bg-blue-50 dark:bg-blue-950/20">
                                <RecurringCostInvoicePanel cost={c} />
                              </td>
                            </tr>
                          )}
                          {isExpanded && (
                            <tr key={`${c.id}-alloc`}>
                              <td colSpan={canManageInvoices ? 10 : 9} className="px-3 pb-3 bg-orange-50">
                                <CostAllocationPanel
                                  costId={c.id}
                                  costType="recurring"
                                  projects={projects}
                                  totalAmount={parseFloat(c.average_amount)}
                                  unit="PLN/mies."
                                />
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 dark:bg-gray-800/50 border-t-2 border-gray-300 dark:border-gray-600">
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-widest">
                        Suma
                      </td>
                      <td className="px-3 py-2 text-right font-bold text-red-700 text-sm">
                        {formatAmount(recurringCosts.reduce((sum, c) => sum + parseFloat(c.average_amount), 0).toFixed(2))} PLN/mies.
                      </td>
                      <td colSpan={canManageInvoices ? 6 : 5} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <Modal open={!!payModal} onClose={() => setPayModal(null)} title="Oznacz koszt jako opłacony" size="sm">
        {payModal && (
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data opłacenia</label>
              <DatePicker
                value={payModal.date}
                onChange={(v) => setPayModal({ ...payModal, date: v })}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={() => setPayModal(null)}>Anuluj</Button>
              <Button
                size="sm"
                loading={markPaid.isPending}
                onClick={async () => {
                  if (!payModal.date) return;
                  await markPaid.mutateAsync({ id: payModal.id, paid_at: payModal.date });
                  setPayModal(null);
                }}
              >
                Zapisz
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {editingCost && (
        <EditRecurringCostModal
          cost={editingCost}
          projects={projects}
          companies={companies}
          onClose={() => setEditingCost(null)}
        />
      )}
    </div>
  );
}

interface CostAllocationPanelProps {
  costId: string;
  costType: "one_time" | "recurring";
  projects: Project[];
  totalAmount: number;
  unit: string;
}

function CostAllocationPanel({ costId, costType, projects, totalAmount, unit }: CostAllocationPanelProps) {
  const otId = costType === "one_time" ? costId : undefined;
  const rcId = costType === "recurring" ? costId : undefined;

  const { data: otStored = [], isLoading: otLoading } = useOneTimeCostAllocations(otId);
  const { data: rcStored = [], isLoading: rcLoading } = useRecurringCostAllocations(rcId);
  const stored = costType === "one_time" ? otStored : rcStored;
  const isLoading = costType === "one_time" ? otLoading : rcLoading;

  const otSet = useSetOneTimeCostAllocations(costType === "one_time" ? costId : "");
  const rcSet = useSetRecurringCostAllocations(costType === "recurring" ? costId : "");
  const otReset = useResetOneTimeCostAllocations(costType === "one_time" ? costId : "");
  const rcReset = useResetRecurringCostAllocations(costType === "recurring" ? costId : "");

  const setMut = costType === "one_time" ? otSet : rcSet;
  const resetMut = costType === "one_time" ? otReset : rcReset;

  // Draft fractions: projectId -> percent string
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);

  if (isLoading) return <p className="text-xs text-gray-400 dark:text-gray-500 py-2">Ładowanie...</p>;

  const storedMap: Record<string, number> = {};
  let hasStored = false;
  for (const a of stored) {
    storedMap[a.project] = parseFloat(a.fraction) * 100;
    hasStored = true;
  }

  // Auto-proportional from total_value
  const totalTv = projects.reduce((s, p) => s + parseFloat(p.total_value || "0"), 0) || 1;
  const autoPct = (p: Project) => (parseFloat(p.total_value || "0") / totalTv) * 100;

  const getEffectivePct = (p: Project) => {
    if (drafts[p.id] !== undefined) return parseFloat(drafts[p.id]) || 0;
    if (hasStored) return storedMap[p.id] ?? 0;
    return autoPct(p);
  };

  const totalPct = projects.reduce((s, p) => s + getEffectivePct(p), 0);
  const hasDirty = Object.keys(drafts).length > 0;

  const handleSave = async () => {
    if (Math.abs(totalPct - 100) > 0.1) {
      setErr(`Suma musi wynosić 100% (aktualnie ${totalPct.toFixed(1)}%).`);
      return;
    }
    await setMut.mutateAsync(
      projects.map((p) => ({ project: p.id, fraction: getEffectivePct(p) / 100 }))
    );
    setDrafts({});
    setErr(null);
  };

  const handleReset = async () => {
    await resetMut.mutateAsync();
    setDrafts({});
    setErr(null);
  };

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-orange-800">
          Dyspozycja kosztu — podział na przedsięwzięcia
          {!hasStored && <span className="ml-1 font-normal text-orange-600">(auto-proporcjonalnie)</span>}
        </p>
        <div className="flex gap-2">
          {hasDirty && (
            <>
              <Button size="sm" loading={setMut.isPending} onClick={handleSave}>Zapisz</Button>
              <Button size="sm" variant="secondary" onClick={() => { setDrafts({}); setErr(null); }}>Anuluj</Button>
            </>
          )}
          {hasStored && !hasDirty && (
            <Button size="sm" variant="secondary" loading={resetMut.isPending} onClick={handleReset}>
              Resetuj (auto)
            </Button>
          )}
        </div>
      </div>

      {projects.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-gray-500">Brak aktywnych przedsięwzięć.</p>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded border border-orange-200 overflow-x-auto">
          <table className="w-full text-xs min-w-[400px]">
            <thead className="bg-orange-50 border-b border-orange-200">
              <tr>
                <th className="text-left px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Przedsięwzięcie</th>
                <th className="text-center px-3 py-1.5 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Udział (%)</th>
                <th className="text-right px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kwota ({unit})</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...projects].sort((a, b) => a.short_name.localeCompare(b.short_name)).map((p) => {
                const pct = getEffectivePct(p);
                const isDirty = drafts[p.id] !== undefined;
                const isAuto = !hasStored && !isDirty;
                const share = (totalAmount * pct / 100).toFixed(2);
                return (
                  <tr key={p.id} className={isDirty ? "bg-yellow-50" : ""}>
                    <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">
                      {p.short_name} — {p.name}
                      {isAuto && <span className="ml-1 text-gray-400 dark:text-gray-500">(auto)</span>}
                      {isDirty && <span className="ml-1 text-yellow-600">*</span>}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          className={`w-full border rounded px-2 py-0.5 text-xs pr-6 text-right ${isDirty ? "border-yellow-400" : "border-gray-300"}`}
                          value={isDirty ? drafts[p.id] : pct.toFixed(1)}
                          onChange={(e) => {
                            setDrafts((prev) => ({ ...prev, [p.id]: e.target.value }));
                            setErr(null);
                          }}
                        />
                        <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-xs">%</span>
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-right text-red-700 font-medium">
                      -{formatAmount(share)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
              <tr>
                <td className="px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-400">Suma</td>
                <td className={`px-3 py-1.5 text-xs font-bold text-right pr-8 ${Math.abs(totalPct - 100) < 0.1 ? "text-green-600" : "text-red-600"}`}>
                  {totalPct.toFixed(1)}%
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
    </div>
  );
}

// ── Invoice cell for one-time costs ──────────────────────────────────────

interface OneTimeCostInvoiceCellProps {
  invoice: InternalCostInvoice | null | undefined;
  onUpload: (file: File) => void;
  onDelete: () => void;
  uploading: boolean;
  deleting: boolean;
}

function OneTimeCostInvoiceCell({ invoice, onUpload, onDelete, uploading, deleting }: OneTimeCostInvoiceCellProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  if (invoice?.url) {
    return (
      <div className="flex items-center gap-1 justify-center">
        <a
          href={invoice.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 hover:underline"
          title={invoice.original_filename}
        >
          PDF
        </a>
        <button
          disabled={deleting}
          onClick={onDelete}
          className="text-[10px] text-red-400 hover:text-red-600 disabled:opacity-50 leading-none"
          title="Usuń fakturę"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <label className={`cursor-pointer inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded border border-dashed ${uploading ? "border-orange-300 text-orange-400" : "border-gray-300 text-gray-400 hover:border-blue-300 hover:text-blue-500"}`} title="Dodaj fakturę PDF">
      {uploading ? "..." : "+ FV"}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        disabled={uploading}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
    </label>
  );
}

// ── Invoice panel for recurring costs ────────────────────────────────────

function RecurringCostInvoicePanel({ cost }: { cost: InternalCostRecurring }) {
  // Legacy per-month PDF invoices (read-only — adding new ones was removed; new
  // invoices are the ``expense_invoices`` below, added via /expenses).
  const { data: legacy = [], isLoading } = useRecurringCostInvoices(cost.id);
  const files = cost.expense_invoices;
  const unskip = useUnskipRecurringMonth();

  return (
    <div className="py-2">
      <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-2">
        Faktury comiesięczne — dodawane w Fakturach kosztowych, tu tylko podgląd
      </p>
      {files.length === 0 ? (
        <p className="text-xs text-gray-400 mb-2">Brak faktur.</p>
      ) : (
        <div className="flex flex-col gap-1 mb-3">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 text-xs bg-white dark:bg-gray-800 border border-blue-100 dark:border-gray-700 rounded px-2 py-1 flex-wrap">
              <span className="text-gray-500 w-24 shrink-0">{f.period_label ?? "—"}</span>
              <span className="tabular-nums text-gray-800 dark:text-gray-200 whitespace-nowrap">{formatAmount(f.amount)} {f.currency}</span>
              <Badge color={EXPENSE_STATUS_COLORS[f.status]}>{EXPENSE_STATUS_LABELS[f.status]}</Badge>
              {f.is_booked && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">Zaksięgowana</span>
              )}
              {f.url ? (
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-auto shrink-0">Pobierz</a>
              ) : (
                <span className="text-gray-400 ml-auto shrink-0">—</span>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Miesiące domknięte bez faktury — liczą się jako 0,00 zł, więc muszą być
          widoczne obok faktur; inaczej „brak wiersza" znaczy dwie różne rzeczy. */}
      {cost.month_skips.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] text-gray-400 mb-1">Miesiące bez faktury (0,00 zł):</p>
          <div className="flex flex-col gap-1">
            {cost.month_skips.map((s) => (
              <div
                key={`${s.period_year}-${s.period_month}`}
                className="flex items-center gap-2 text-xs bg-white dark:bg-gray-800 border border-dashed border-gray-200 dark:border-gray-700 rounded px-2 py-1"
              >
                <span className="text-gray-500 w-24 shrink-0">{s.period_label ?? "—"}</span>
                <span className="text-gray-400 truncate">{s.note || "faktury nie było"}</span>
                <button
                  onClick={() =>
                    unskip.mutate({
                      id: cost.id,
                      period_year: s.period_year,
                      period_month: s.period_month,
                    })
                  }
                  className="ml-auto shrink-0 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:underline"
                >
                  Cofnij
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Legacy PDF attachments (no amount) — read-only download of historical files. */}
      {!isLoading && legacy.length > 0 && (
        <div className="mt-2">
          <p className="text-[11px] text-gray-400 mb-1">Stare faktury PDF (archiwalne):</p>
          <div className="flex flex-col gap-1">
            {legacy.map((inv) => (
              <div key={inv.id} className="flex items-center gap-2 text-xs bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 rounded px-2 py-1">
                <span className="text-gray-500 w-16 shrink-0 font-mono">{inv.month ? `${inv.month.slice(5, 7)}.${inv.month.slice(0, 4)}` : "—"}</span>
                <a href={inv.url ?? "#"} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:underline truncate max-w-[240px]" title={inv.original_filename}>
                  {inv.original_filename}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
